import { observable, computedFrom, autoinject, TaskQueue, children } from 'aurelia-framework';
import { NodeModel } from '../../models/node-model';
import { ServiceStructureService } from '../services/service-structure-service';
import { IScopeAM, ScopeService } from '../scopes/scope-service';
import {
	OrganizationalUnitService,
	OrganizationalNodeModel
} from '../customers/organizational-units/organizational-unit-service';
import {
	ReportTemplate,
	ReportTemplateRowType,
	IGeneratedRow,
	IRowGeneratorParameters,
	IReportTemplateRow,
	IReportTemplateColumn,
	ReportTemplateColumnType,
	CommonRowsSet,
	ICommonRow, ReportFinancialPrecision,
} from './models/report-template';
import { MunicipalityService, CustomerMunicipality } from '../municipalities/municipality-service';
import { Router } from 'aurelia-router';
import { NewReportPreviewSwapStore } from './report-route-store';
import { GlobalContextService } from '../../components/global-select/global-context-service';
import { ScopeType, ScopeTypeService } from '../scopes/scope-type-service';
import { Disposable } from 'aurelia-binding';
import { RowGeneratorIncludes, useAccountGroupSeparationByDefault, useAccountSeparationByDefault, useTypeSeparationAvailableByDefault } from './report-components/row-generator-includes';
import { RowGeneratorCommonRows } from "./models/RowGeneratorCommonRows";
import { ReportService } from "./report-api";
import { EventAggregator } from "aurelia-event-aggregator";
import { GlobalCustomerSelect } from "../../components/global-select/customer/global-customer-select";
import { CustomerService, CustomerType } from '../customers/customer-service';
import { isNumber, uniq } from 'lodash';
import { v4 as uuid } from 'uuid';
import { FiscalYearService } from '../customers/fiscal-year-service';
import { INameIdAM } from '../../models/NameIdAM';
import { AccountingDimensionService } from '../accounting-dimensions/accounting-dimension-api';

@autoinject
export class NewReportWizard {
	submitting = false;
	isInitializing = true;
	@observable template: ReportTemplate;

	private customerId: string;
	private primaryServiceStructureId: string;
	@observable private serviceStructureId: string;
	private organizationId: string;

	private serviceStructures: INameIdAM[];

	private selectedMunicipalitiesFromCachedTemplate: string[] = [];
	private selectedMunicipalities: string[] = [];
	private selectedScopesFromCachedTemplate: string[] = [];
	private selectedScopes: string[] = [];

	private activeCustomerSubscribtion: Disposable;
	private defaultScopeTypeId: string;
	@observable includeServices: boolean;
	@observable includeCostCalculations: boolean;
	@observable includeInternalEntries: boolean;
	@observable includePassedOnEntries: boolean;

	serviceStructureNodes: NodeModel[] = null;
	organizationalStructureNodes: OrganizationalNodeModel[] = null;
	scopes: IScopeAM[];
	scopeTypes: ScopeType[];
	showDimensionFiltersElementOption: boolean = null;
	showDimensionSeparationOption: boolean = null;

	identifiersAreMissing: boolean = false;
	scopesAreMissing: boolean = false;
	scopeTypeIdIsMissing: boolean = false;

	availableMunicipalities: CustomerMunicipality[];
	customerType: CustomerType;

	showServiceStructureSelect: boolean = false;
	private ensuringData: boolean = false;

	private reportFinancialPrecision: ReportFinancialPrecision;
	
	@computedFrom('template')
	get generatedRow(): IGeneratedRow {
		return this.template && this.template.content && this.template.content.rows.length
			? this.template.content.rows[0] as IGeneratedRow
			: null;
	}

	@computedFrom('generatedRow.generator', 'serviceStructureId')
	get requiredServiceStructureIdForScopeSelection(): string | undefined {

		return (this.generatedRow && this.generatedRow.generator == ReportTemplate.generatorType_servicestructure)
			? this.serviceStructureId
			: undefined;
	}

	@computedFrom('generatedRow', 'generatedRow.parameters')
	get generatorParameters(): IRowGeneratorParameters {
		if (this.generatedRow == null) { return null; }

		return this.generatedRow.parameters;
	}

	@computedFrom('generatedRow', 'generatedRow.generator')
	get isServiceStructure(): boolean {
		return this.generatedRow && this.generatedRow.generator === ReportTemplate.generatorType_servicestructure;
	}

	@computedFrom('generatedRow', 'generatedRow.generator')
	get isOrganizationalStructure(): boolean {
		return !this.generatedRow || this.generatedRow.generator === ReportTemplate.generatorType_organizational;
	}

	@computedFrom('serviceStructureId', 'serviceStructures')
	get serviceStructure(): INameIdAM {
		return this.serviceStructures
			? this.serviceStructures.find(ss => ss.id == this.serviceStructureId)
			: undefined;
	}

	constructor(
		private serviceStructureService: ServiceStructureService,
		private organizationalUnitService: OrganizationalUnitService,
		private municipalityService: MunicipalityService,
		private scopeService: ScopeService,
		private fiscalYearService: FiscalYearService,
		private reportService: ReportService,
		private q: TaskQueue,
		private router: Router,
		private routeStore: NewReportPreviewSwapStore,
		private globalContext: GlobalContextService,
		private scopeTypeService: ScopeTypeService,
		private ea: EventAggregator,
		private rowGeneratorCommonRows: RowGeneratorCommonRows,
		private customerService: CustomerService,
		private accountingDimensionService: AccountingDimensionService) {
	}

	changeStructureType(structureType: string) {
		const row = this.generatedRow;
		row.generator = structureType;

		var isOrganizational = (structureType == ReportTemplate.generatorType_organizational);

		// Set default values
		this.serviceStructureId = this.primaryServiceStructureId;
		this.includeServices = !isOrganizational;

		this.template.content.source.filters.includeUntargetedFinancialEntries = isOrganizational;
		if (row.parameters) {
			row.parameters.includeMeasures = true;
			row.parameters.includePerformanceIndicators = true;
			row.parameters.includeCostCenters = isOrganizational;
			row.parameters.identifiers = null;
		}

		this.ensureDataAvailability();
		this.q.flushTaskQueue();
		this.q.queueTask(() => { if (row.parameters) { row.parameters.identifiers = null } });
	}

	handleKeypress(e: KeyboardEvent, structureType: string) {
		e.stopPropagation();

		if (e.code === "Space" || e.code === "Enter") {
			this.changeStructureType(structureType);
		}
	}

	toggleServiceStructureSelectVisibility() {
		this.showServiceStructureSelect = !this.showServiceStructureSelect;
	}

	private async ensureDataAvailability() {

		this.ensuringData = true;

		if (!this.scopes) {

			var fiscalYears = await this.fiscalYearService.getFiscalYearsForCustomer(this.customerId);
			var latestFiscalYear = fiscalYears[0];
			this.primaryServiceStructureId = latestFiscalYear.primaryServiceStructureId;
			if (!this.serviceStructureId) {
				this.serviceStructureId = latestFiscalYear.primaryServiceStructureId;
			}
			this.organizationId = latestFiscalYear.organizationId;

			this.scopes = await this.scopeService.getScopesForCustomer(this.customerId);
		}

		if (!this.serviceStructures) {
			this.serviceStructures = await this.serviceStructureService.getServiceStructuresOfCustomer(this.customerId);
		}

		if (this.isServiceStructure && !this.serviceStructureNodes) {
			this.serviceStructureNodes =
				await this.serviceStructureService.getServiceStructureForCustomer(this.serviceStructureId, this.customerId);
		}

		if (this.isOrganizationalStructure && !this.organizationalStructureNodes) {
			this.organizationalStructureNodes =
				await this.organizationalUnitService.getOrganizationalUnits(this.organizationId, false);
		}

		if (!this.scopeTypes) {
			this.scopeTypes = await this.scopeTypeService.getScopeTypes();
		}

		if (this.showDimensionFiltersElementOption == null) {
			this.showDimensionFiltersElementOption = await this.reportService.hasCustomerReportDimensionFilters(this.customerId);
		}

		if (this.showDimensionSeparationOption == null) {
			this.showDimensionSeparationOption = (await this.accountingDimensionService.getDimensionsForCustomer(this.customerId)).length > 0;
		}

		this.ensuringData = false;
	}

	serviceStructureIdChanged() {
		this.serviceStructureNodes = null;
		this.generatedRow.parameters.serviceStructureId = this.serviceStructureId;
		if (!this.ensuringData) {
			this.ensureDataAvailability();
		}
	}

	includeCostCalculationsChanged() {
		if (this.template.content.source) this.template.content.source.filters.excludeCostCalculations = !this.includeCostCalculations;
	}
	includeInternalEntriesChanged() {
		if (this.template.content.source) this.template.content.source.filters.excludeInternalEntries = !this.includeInternalEntries;
	}
	includePassedOnEntriesChanged() {
		if (this.template.content.source) this.template.content.source.filters.excludePassedOnEntries = !this.includePassedOnEntries;
	}

	includeServicesChanged() {
		if (this.generatorParameters) this.generatorParameters.includeServices = this.includeServices;
	}

	municipalitySelectionChanged(selectedItems: string[]) {
		this.selectedMunicipalities = selectedItems;
	}

	scopeSelectionChanged(selectedItems: string[]) {
		this.selectedScopes = selectedItems;

		if (selectedItems.length > 0) {

			const scopeSelections = this.scopes.filter(s => selectedItems.indexOf(s.id) > -1);
			const serviceStructureIds = uniq(scopeSelections.map(s => s.serviceStructureId));

			// Service structure
			if (serviceStructureIds.length == 0) {
				this.generatorParameters.serviceStructureId = this.primaryServiceStructureId;
			} else if (serviceStructureIds.length == 1) {
				this.generatorParameters.serviceStructureId = serviceStructureIds[0];
			} else {
				this.generatorParameters.serviceStructureId = undefined;
				this.generatorParameters.includeServices = false;
				this.generatedRow.parameters.includeMeasures = false;
				this.generatedRow.parameters.includePerformanceIndicators = false;
			}

			// Scope type
			if (!scopeSelections.find(s => s.scopeTypeId === this.generatorParameters.scopeTypeId)) {
				this.generatorParameters.scopeTypeId = scopeSelections[0].scopeTypeId;
			}
		}
		else {
			this.generatorParameters.scopeTypeId = undefined;
		}
	}

	generateReportColumnsData(): IReportTemplateColumn[] {

		const scopesInCorrectOrder = this.selectedScopes.reverse();
		const columns = [];

		if (this.selectedMunicipalities.length && (this.includeCostCalculations || this.isServiceStructure)) {

			for (let i = 0; i < this.selectedMunicipalities.length; i++) {
				const code = this.selectedMunicipalities[i];
				const municipality = isNumber(code) ? parseInt(code) : code;

				for (let j = 0; j < scopesInCorrectOrder.length; j++) {
					const column = {
						id: uuid(),
						type: ReportTemplateColumnType.Data,
						municipality: municipality,
						scope: scopesInCorrectOrder[j]
					}
					columns.push(column);
				}
			}
		}
		else {
			for (let i = 0; i < scopesInCorrectOrder.length; i++) {
				const column = {
					id: uuid(),
					type: ReportTemplateColumnType.Data,
					scope: scopesInCorrectOrder[i]
				}
				columns.push(column);
			}
		}

		return columns;
	}

	validateForm() {
		const params = (this.template.content.rows[0] as IGeneratedRow).parameters;

		if (!params.identifiers || !params.identifiers.length) {
			this.identifiersAreMissing = true;
		}
		if (this.selectedMunicipalities.length && !this.selectedScopes.length) {
			this.scopesAreMissing = true;
		}
		if (!params.scopeTypeId && (params.includeMeasures || params.includePerformanceIndicators)) {
			this.scopeTypeIdIsMissing = true;
		}

		return !(this.identifiersAreMissing || this.scopesAreMissing || this.scopeTypeIdIsMissing);
	}

	async generateReport() {
		this.identifiersAreMissing = false;
		this.scopesAreMissing = false;
		this.scopeTypeIdIsMissing = false;

		if (this.isServiceStructure) {
			this.template.content.source.filters.excludeCostCalculations = false;
			this.generatorParameters.includeServices = true;
		}

		this.template.content.source.filters.includeFinancialEntriesInAccountClassOther =
			this.generatorParameters.commonRowsSet === CommonRowsSet.IncomeStatement;

		if (!this.validateForm()) { return; }

		this.template.content.columns = this.generateReportColumnsData();
		this.template.content.source.filters.financialPrecision = this.reportFinancialPrecision;

		this.routeStore.store = this.template;
		this.submitting = true;
		this.router.navigateToRoute('preview-report', { customerId: this.customerId });
	}

	async attached() {

		this.customerId = this.globalContext.getId('customer');
		await this.initializeForCustomer();
		this.isInitializing = false;

		this.activeCustomerSubscribtion = this.ea.subscribe(`itemChanged-${GlobalCustomerSelect.customerItemName}`, async (customer: any) => {
			if (customer.id != this.customerId) {
				this.routeStore.store = null;
			}
			this.customerId = customer.id;
			this.initializeForCustomer();
		});

	}

	async initializeForCustomer() {

		if (!this.customerId) { return; }

		this.scopes = null;
		this.serviceStructures = null;
		this.showDimensionFiltersElementOption = null;
		this.showDimensionSeparationOption = null;
		this.serviceStructureNodes = null;
		this.organizationalStructureNodes = null;

		// Populating the tree and selecting nodes needs to be done in order:
		// 1. Assign the generator for the generated row.
		// 2. Populate the data needed to create the tree nodes. (ensureDataAvailability)
		// 3. Assign the whole template: From cache if there is one, or create a new default report template.

		const cachedTemplate = this.routeStore.fromCache<ReportTemplate>();
		const hasCachedGeneratorRow = cachedTemplate != null
			&& cachedTemplate.content != null
			&& cachedTemplate.content.rows.length > 0
			&& cachedTemplate.content.rows[0].type === ReportTemplateRowType.GeneratedRows;

		var cachedGeneratorRow = hasCachedGeneratorRow ? (cachedTemplate.content.rows[0] as IGeneratedRow) : null;

		// Preliminary settings, these are in user while fetching data.
		// Selected identifiers (generatorRow.parameters.identifiers) should not be set yet, because treeview doesn't contain anything -> nothing gets selected.
		this.template = new ReportTemplate({
			content: {
				rows: [
					{
						type: ReportTemplateRowType.GeneratedRows,
						generator: hasCachedGeneratorRow ? cachedGeneratorRow.generator : ReportTemplate.generatorType_organizational,
						parameters: {},
					} as IGeneratedRow
				],
				source: (cachedTemplate && cachedTemplate.content.source) || {
					filters: {
						includeUntargetedFinancialEntries: true,
						excludeCostCalculations: true
					}
				}
			}
		});

		// Mark ensuring started to prevent duplicate ensurances - the actual ensure call is a few rows below
		this.ensuringData = true;

		// User might change structure type (=>changeStructureType) of a new report while ensuring data
		/// => we need a) to have template.content.source.filters initialized, and b) to read the potentially set generator type after data ensurance.
		this.customerType = await this.customerService.getCustomerType(this.customerId);
		this.availableMunicipalities = await this.municipalityService.getCurrentMunicipalities(this.customerId);
		this.serviceStructureId = hasCachedGeneratorRow ? cachedGeneratorRow.parameters.serviceStructureId : undefined;
		await this.ensureDataAvailability();

		if (cachedTemplate != null) {
			this.template = cachedTemplate;
			this.selectedMunicipalitiesFromCachedTemplate = uniq(this.template.content.columns
				.map(c => (c.municipality ? c.municipality.toString() : null)));
			this.selectedMunicipalities = this.selectedMunicipalitiesFromCachedTemplate.slice();
			this.selectedScopesFromCachedTemplate = uniq(this.template.content.columns.map(c => c.scope));
			this.selectedScopes = this.selectedScopesFromCachedTemplate.slice();
			this.includeCostCalculations = !this.template.content.source.filters.excludeCostCalculations;
			this.includeInternalEntries = !this.template.content.source.filters.excludeInternalEntries;
			this.includePassedOnEntries = !this.template.content.source.filters.excludePassedOnEntries;
		} else {

			// Defaults for a new report (either organizational or service report): 

			this.selectedScopes = [];
			this.selectedMunicipalitiesFromCachedTemplate = [];
			this.selectedScopesFromCachedTemplate = [];
			this.selectedMunicipalities = [];
			this.includeCostCalculations = false;
			this.includeInternalEntries = true;
			this.includePassedOnEntries = true;

			const generatorType = this.generatedRow.generator || ReportTemplate.generatorType_organizational; // May have been manually changed during ensureDataAvailability
			let source = this.template.content.source;
			source.filters.includeUntargetedFinancialEntries = generatorType === ReportTemplate.generatorType_organizational;

			const rowSet = CommonRowsSet.Basic;
			const commonRows: ICommonRow[] = this.rowGeneratorCommonRows
				.getOptions(CommonRowsSet.Basic)
				.filter(o => o.isCheckedByDefault)
				.map(o => ({
					rowTemplate: o.row,
					accountGroupSeparation: useAccountGroupSeparationByDefault(o, rowSet),
					accountSeparation: useAccountSeparationByDefault(o, rowSet),
					typeSeparation: useTypeSeparationAvailableByDefault(o, rowSet),
				}));

			this.template = new ReportTemplate({
				isPrivate: false,
				content: {
					rows: [{
						type: ReportTemplateRowType.GeneratedRows,
						generator: generatorType,
						parameters: {
							includeCostCenters: generatorType === ReportTemplate.generatorType_organizational,
							includeCostCenterDimensionFilters: this.showDimensionFiltersElementOption,
							includeMeasures: true,
							includePerformanceIndicators: true,
							includeSumSection: false,
							includeServices: false,
							scopeTypeId: this.defaultScopeTypeId,
							serviceStructureId: this.serviceStructureId,
							organizationId: this.organizationId,
							identifiers: [],
							commonRowsSet: rowSet,
							selectedCommonRows: commonRows,
						}
					} as IGeneratedRow],
					source: source
				},
			});
		}
		this.includeServices = this.generatorParameters.includeServices;
	}

	updateIncludes(parameters: IRowGeneratorParameters) {

		// Don't handle identifiers here, they are not edited with includes and might overwrite changes made elsewhere
		const { identifiers, ...parametersToHandle } = parameters;
		Object.assign(this.generatedRow.parameters, parametersToHandle);
	}

	detached() {
		this.activeCustomerSubscribtion.dispose();
	}
	
	updateFinancialPrecision(precision: ReportFinancialPrecision) {
		this.reportFinancialPrecision = precision;
	}

	@children('row-generator-includes') rowGeneratorIncludes: RowGeneratorIncludes;
}
